import React from 'react';
import { FileText, ChevronUp, Printer, Download } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения детальной информации о расчете НДС
 */
const TaxDetails = ({ results, formData, setShowDetails }) => {
  if (!results) return null;

  // Функция для определения типа ставки НДС
  const getTaxRateLabel = (taxRate) => {
    if (taxRate === 'exempt') return 'Без НДС';
    return `${taxRate}%`;
  };

  // Функция для определения класса CSS для бейджа ставки НДС
  const getTaxRateBadgeClass = (taxRate) => {
    const baseClass = 'tax-rate-badge';
    
    if (taxRate === '20' || taxRate === '20/120') return `${baseClass} tax-rate-badge-20`;
    if (taxRate === '10' || taxRate === '10/110') return `${baseClass} tax-rate-badge-10`;
    if (taxRate === '0') return `${baseClass} tax-rate-badge-0`;
    if (taxRate === 'exempt') return `${baseClass} tax-rate-badge-exempt`;
    
    return `${baseClass} bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300`;
  };

  // Функция для печати результатов
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      alert('Пожалуйста, разрешите всплывающие окна для печати');
      return;
    }
    
    // Формируем содержимое для печати
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="ru">
      <head>
        <meta charset="UTF-8">
        <title>Расчет НДС</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px; }
          h1 { font-size: 24px; margin-bottom: 20px; }
          h2 { font-size: 18px; margin-top: 30px; margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
          th { background-color: #f2f2f2; }
          .text-right { text-align: right; }
          .total-row { font-weight: bold; background-color: #f8f8f8; }
          .meta-info { margin-bottom: 20px; font-size: 14px; color: #666; }
          .meta-info p { margin: 5px 0; }
          .footer { margin-top: 40px; font-size: 12px; color: #666; border-top: 1px solid #eee; padding-top: 10px; }
        </style>
      </head>
      <body>
        <h1>Расчет НДС</h1>
        
        <div class="meta-info">
          <p>Дата расчета: ${new Date().toLocaleDateString('ru-RU')}</p>
          <p>Тип расчета: ${formData.calculationType === 'add' ? 'Начисление НДС' : 'Выделение НДС из суммы'}</p>
          ${!results.isMultipleItems ? `<p>Ставка НДС: ${getTaxRateLabel(results.taxRateOriginal)}</p>` : ''}
        </div>
        
        ${!results.isMultipleItems ? `
        <h2>Результаты расчета</h2>
        <table>
          <tr>
            <td>Сумма без НДС</td>
            <td class="text-right">${formatCurrency(results.amountWithoutVAT)}</td>
          </tr>
          <tr>
            <td>НДС</td>
            <td class="text-right">${results.isExempt ? 'Без НДС' : formatCurrency(results.vatAmount)}</td>
          </tr>
          <tr>
            <td>Сумма с НДС</td>
            <td class="text-right">${formatCurrency(results.amountWithVAT)}</td>
          </tr>
          ${formData.includeOtherTaxes && results.otherTaxAmount > 0 ? `
          <tr>
            <td>${formData.otherTaxName || 'Дополнительный налог'}</td>
            <td class="text-right">${formatCurrency(results.otherTaxAmount)}</td>
          </tr>
          <tr class="total-row">
            <td>Итого к оплате</td>
            <td class="text-right">${formatCurrency(results.totalPayable)}</td>
          </tr>
          ` : ''}
        </table>
        ` : `
        <h2>Детализация расчета</h2>
        <table>
          <thead>
            <tr>
              <th>Наименование</th>
              <th>Ставка НДС</th>
              <th class="text-right">Сумма без НДС</th>
              <th class="text-right">НДС</th>
              <th class="text-right">Сумма с НДС</th>
            </tr>
          </thead>
          <tbody>
            ${results.items.map(item => `
            <tr>
              <td>${item.description}</td>
              <td>${getTaxRateLabel(item.taxRate)}</td>
              <td class="text-right">${formatCurrency(item.amountWithoutVAT)}</td>
              <td class="text-right">${item.isExempt ? 'Без НДС' : formatCurrency(item.vatAmount)}</td>
              <td class="text-right">${formatCurrency(item.amountWithVAT)}</td>
            </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="2">ИТОГО:</td>
              <td class="text-right">${formatCurrency(results.totalAmountWithoutVAT)}</td>
              <td class="text-right">${formatCurrency(results.totalVATAmount)}</td>
              <td class="text-right">${formatCurrency(results.totalAmountWithVAT)}</td>
            </tr>
          </tbody>
        </table>
        ${formData.includeOtherTaxes && results.otherTaxAmount > 0 ? `
        <table>
          <tr>
            <td>${formData.otherTaxName || 'Дополнительный налог'}</td>
            <td class="text-right">${formatCurrency(results.otherTaxAmount)}</td>
          </tr>
          <tr class="total-row">
            <td>Итого к оплате</td>
            <td class="text-right">${formatCurrency(results.totalPayable)}</td>
          </tr>
        </table>
        ` : ''}
        `}
        
        <div class="footer">
          <p>Расчет произведен с помощью калькулятора НДС | Gensio</p>
          <p>Данный документ не является счетом-фактурой и носит информационный характер</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Печать после загрузки документа
    setTimeout(() => {
      printWindow.print();
      // printWindow.close();
    }, 300);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-5">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <FileText className="w-5 h-5 mr-2 text-indigo-500" />
          Детализация расчета НДС
        </h3>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handlePrint}
            icon={<Printer className="w-4 h-4" />}
            variant="outline"
          >
            Печать
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setShowDetails(false)}
            icon={<ChevronUp className="w-4 h-4" />}
          >
            Скрыть
          </Button>
        </div>
      </div>

      {!results.isMultipleItems ? (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg overflow-hidden">
          <table className="w-full border-collapse">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Показатель</th>
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Значение</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Тип расчета</td>
                <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                  {formData.calculationType === 'add' ? 'Начисление НДС' : 'Выделение НДС из суммы'}
                </td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Ставка НДС</td>
                <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                  <span className={getTaxRateBadgeClass(results.taxRateOriginal)}>
                    {getTaxRateLabel(results.taxRateOriginal)}
                  </span>
                </td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Сумма без НДС</td>
                <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                  {formatCurrency(results.amountWithoutVAT)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  {results.isExempt ? 'НДС' : 'Сумма НДС'}
                </td>
                <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                  {results.isExempt 
                    ? <span className="text-gray-500 dark:text-gray-400">Без НДС</span>
                    : formatCurrency(results.vatAmount)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Сумма с НДС</td>
                <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                  {formatCurrency(results.amountWithVAT)}
                </td>
              </tr>
              {formData.includeOtherTaxes && results.otherTaxAmount > 0 && (
                <>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {formData.otherTaxName || 'Дополнительный налог'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                      {formatCurrency(results.otherTaxAmount)}
                    </td>
                  </tr>
                  <tr className="bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30">
                    <td className="px-4 py-3 font-medium text-indigo-700 dark:text-indigo-300">
                      Итого к оплате
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-indigo-700 dark:text-indigo-300">
                      {formatCurrency(results.totalPayable)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Расчет НДС по каждой позиции товаров/услуг
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full border-collapse min-w-full">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">№</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Наименование</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-700 dark:text-gray-300">Ставка НДС</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300">Сумма без НДС</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300">НДС</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300">Сумма с НДС</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {results.items.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{index + 1}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {item.description || `Товар/услуга ${index + 1}`}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className={getTaxRateBadgeClass(item.taxRate)}>
                        {getTaxRateLabel(item.taxRate)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(item.amountWithoutVAT)}
                    </td>
                    <td className="px-4 py-2 text-right text-sm text-gray-900 dark:text-white">
                      {item.isExempt 
                        ? <span className="text-gray-500 dark:text-gray-400">Без НДС</span>
                        : formatCurrency(item.vatAmount)}
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(item.amountWithVAT)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <td className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300" colSpan="3">
                    ИТОГО:
                  </td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-white">
                    {formatCurrency(results.totalAmountWithoutVAT)}
                  </td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-white">
                    {formatCurrency(results.totalVATAmount)}
                  </td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-white">
                    {formatCurrency(results.totalAmountWithVAT)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {formData.includeOtherTaxes && results.otherTaxAmount > 0 && (
            <div className="mt-4 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 p-4 border border-indigo-100 dark:border-indigo-800">
              <div className="flex justify-between items-center">
                <span className="font-medium text-indigo-700 dark:text-indigo-300">
                  {formData.otherTaxName || 'Дополнительный налог'}:
                </span>
                <span className="font-medium text-indigo-700 dark:text-indigo-300">
                  {formatCurrency(results.otherTaxAmount)}
                </span>
              </div>
              <div className="flex justify-between items-center mt-2 font-bold">
                <span className="text-indigo-800 dark:text-indigo-200">Итого к оплате:</span>
                <span className="text-indigo-800 dark:text-indigo-200">{formatCurrency(results.totalPayable)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Блок с дополнительной информацией */}
      <div className="mt-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Формула расчета:</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {formData.calculationType === 'add' ? (
            <>
              Сумма с НДС = Сумма без НДС × (1 + Ставка НДС / 100)<br />
              НДС = Сумма без НДС × (Ставка НДС / 100)
            </>
          ) : (
            <>
              Сумма без НДС = Сумма с НДС / (1 + Ставка НДС / 100)<br />
              НДС = Сумма с НДС - Сумма без НДС
              {(results.taxRateOriginal === '20/120' || results.taxRateOriginal === '10/110') && (
                <><br />или НДС = Сумма с НДС × (Расчетная ставка)</>
              )}
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default TaxDetails;